'use client';
import { SortFilterBarButton } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/SortFilterBarButton';
import { useCmsClientContext } from '@hyphengroup/cms-components/utils/cmsPageContext';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { FilterInput } from '../../../filterInput';
import { LoanSearchParams } from '../../helpers/loanSearchParams';
import { useLoanSearchParams } from '../../helpers/useLoanSearchParams';
import { TRANSLATIONS } from '../../translations';
import { PageType, VerticalType } from '../../types';
import { readSlotComponentIndex } from '../slotComponentIndex';
import { type LoanDetailsField, trackLoanDetailsFilled } from './tracking';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

interface PersonalLoanFilterProps {
  show: boolean;
  defaultParams: LoanSearchParams;
  onFinished?: (loanParams: LoanSearchParams) => void;
  onlyListen: boolean;
  min_loan_amount: number | undefined;
  max_loan_amount: number | undefined;
  min_loan_tenure: number | undefined;
  max_loan_tenure: number | undefined;
}

export const PersonalLoanFilter = (props: PersonalLoanFilterProps) => {
  const context = useClientContext();

  const isFirstRender = useRef(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const { loanSearchParams } = useLoanSearchParams(props.defaultParams);
  const { pageType } = usePageDataContext();
  // Block vertical, not the page's: a PL block can sit on another vertical's page (CMS-1987).
  const { vertical: section } = useCmsClientContext();

  const staticText = getClientTranslations(TRANSLATIONS, context);
  const amountMin = props.min_loan_amount ?? 1000;
  const amountMax = props.max_loan_amount;
  const tenureMin = props.min_loan_tenure ?? 6;
  const tenureMax = props.max_loan_tenure;

  const amountHelpTextConditions = {
    max: getMaxLoanAmount({
      initialMaxLoanAmount: props.max_loan_amount,
      brand: context.brand,
      pageType: pageType,
      section,
    }),
    min: amountMin,
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    try {
      props.onFinished?.(loanSearchParams);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.debug('ERROR', e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanSearchParams.amount, loanSearchParams.tenure, props.onlyListen]);

  const trackFilled = (field: LoanDetailsField) => (value: string) =>
    trackLoanDetailsFilled({
      field,
      value,
      componentIndex: readSlotComponentIndex(rootRef.current),
    });

  const inputClassname =
    '!w-[160px] md:!w-full !min-w-[160px] md:!min-w-[200px]';
  return (
    <div
      ref={rootRef}
      style={{
        overflow: 'hidden',
      }}
      className={classNames('flex flex-row gap-2 py-4 bg-fill/base md:gap-4', {
        hidden: !props.show,
      })}
    >
      <FilterInput
        onFinished={props.onFinished}
        title={tr('personal_loan_amount', staticText, {
          currency: context.currencyDisplay,
        })}
        help={tr('personal_loan_amount_help', staticText, {
          currency: context.currencyDisplay,
          max_amount: currencyFormatterNoDecimal.format(
            amountHelpTextConditions.max,
          ),
          min_amount: currencyFormatterNoDecimal.format(
            amountHelpTextConditions.min,
          ),
        })}
        inputClassname={inputClassname}
        defaultValue={props.defaultParams}
        searchParam="amount"
        onCommit={trackFilled('amount')}
        prefix={context.currencyDisplay}
        min={amountMin}
        max={amountMax}
      />
      <FilterInput
        title={staticText.personal_loan_tenure}
        help={tr('personal_loan_tenure_help', staticText, {
          currency: context.currencyDisplay,
          min_tenure: tenureMin.toString(),
          max_tenure: (tenureMax ?? 84).toString(),
        })}
        inputClassname={inputClassname}
        onFinished={props.onFinished}
        defaultValue={props.defaultParams}
        min={tenureMin}
        max={tenureMax}
        searchParam="tenure"
        onCommit={trackFilled('tenure')}
      />
      {/* In every instance, not only the showing ones: the slot displays its
          first node whatever its `show`, and the panel's own pill gives way as
          soon as any instance has a button on screen (CMS-2072). */}
      <SortFilterBarButton />
    </div>
  );
};

function getMaxLoanAmount(props: {
  initialMaxLoanAmount?: number;
  brand: string;
  pageType: PageType;
  section?: VerticalType;
}) {
  if (props.initialMaxLoanAmount) {
    return props.initialMaxLoanAmount;
  }

  if (props.pageType === 'article' && props.section === 'PERSONAL_LOANS') {
    return 100000;
  }
  if (props.brand === 'singsaver') {
    return 100000;
  }

  return 600000;
}
